import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import RespiratoryRate from "../../icons/RespiratoryRate";
import {
  pickSpirometryResult,
  SPIROMETRY_PREDICTION_SOURCES,
} from "../../../utils/results/spirometry";
import { formatValidatedMeasurement } from "../../../utils/results/validatedRanges";
import { SignalChart } from "../../charts/SignalChart";
import FlowLoopChart from "../../charts/FlowLoopChart";
import MetricsKVTable from "../MetricsKVTable";
import FlowVolumeLoopsSection from "../FlowVolumeLoopsSection";
import SignalQuality from "../../icons/SignalQuality";
import { trackEvent } from "../../../utils/analytics/track";
import { SHOW_FULL_RESULTS } from "../../../config/results";
import { formatRecordingIdDateParts } from "../../../utils/results/recordingIdTime";
import LungIcon from "../../icons/LungIcon";
import Spacer from "../../Spacer";
import {
  ResultSummaryRow,
  ResultSummarySection,
} from "../ResultSummarySection";
import ResultQualityNotice from "../ResultQualityNotice";
import ResultViewScaffold from "../ResultViewScaffold";
import {
  buildSpirometryAdditionalMetricsFields,
  buildSpirometryAdditionalMetricsRecord,
  getSpiroQualityMessage,
  getSpirometryQualityHintKeys,
  isSpirometryResultReady,
} from "../../../utils/results/spirometryView";
import { formatPassFailValue } from "../../../utils/results/resultQuality";
import {
  getPointSeriesMaxSecs,
  getTimeSeriesMaxSecs,
  normaliseTimeValuePoints,
} from "../../../utils/results/viewTimeSeries";

function SpirometryResultViewImpl({
  data,
  docId,
  predictionSource = SPIROMETRY_PREDICTION_SOURCES.LUNGDX,
  onFinish,
}) {
  const { t, i18n } = useTranslation();

  const {
    fev1,
    fvc,
    fev1FvcRatio,
    pef,
    rr,
    signalQuality,
    spirometryQuality,
    spirometryQualityChecks,
    zeroedMetrics,
    additionalMetrics,
    processedSignal,
  } = useMemo(
    () => pickSpirometryResult(data, { predictionSource }),
    [data, predictionSource],
  );

  const recordingTime = useMemo(
    () =>
      formatRecordingIdDateParts(
        docId,
        i18n.resolvedLanguage || i18n.language || "en-GB",
      ),
    [docId, i18n.resolvedLanguage, i18n.language],
  );

  const qualityMsg = useMemo(
    () =>
      getSpiroQualityMessage(
        { signalQuality, spirometryQuality, zeroedMetrics },
        t,
      ),
    [signalQuality, spirometryQuality, zeroedMetrics, t],
  );

  const signalQualityLabel = useMemo(
    () => formatPassFailValue(signalQuality, t),
    [signalQuality, t],
  );

  const spirometryQualityLabel = useMemo(
    () => formatPassFailValue(spirometryQuality, t),
    [spirometryQuality, t],
  );

  const signalQualityIsPass = signalQuality === "pass";
  const resultQualityIsDisabled = Boolean(qualityMsg || !signalQualityIsPass);
  const resultQualityNotice =
    qualityMsg ||
    (!signalQualityIsPass
      ? {
          title: t("result.spiro.qualityFailTitle", "Low signal quality."),
          subtitle: t("result.spiro.qualityFailSubtitle", "Please try again."),
        }
      : null);

  const resultQualityHints = useMemo(
    () =>
      getSpirometryQualityHintKeys(spirometryQualityChecks).map((checkKey) =>
        t(`result.spiro.qualityHints.items.${checkKey}`),
      ),
    [spirometryQualityChecks, t],
  );

  const fev1Display = useMemo(
    () => formatValidatedMeasurement("fev1", fev1),
    [fev1],
  );

  const pefDisplay = useMemo(
    () => formatValidatedMeasurement("pef", pef),
    [pef],
  );

  const fvcDisplay = useMemo(
    () => formatValidatedMeasurement("fvc", fvc),
    [fvc],
  );

  const fev1FvcRatioDisplay = useMemo(
    () => formatValidatedMeasurement("fev1FvcRatio", fev1FvcRatio),
    [fev1FvcRatio],
  );

  const rrDisplay = useMemo(
    () =>
      typeof rr?.average === "number" && Number.isFinite(rr.average)
        ? rr.average.toFixed(0)
        : typeof rr?.mean === "number" && Number.isFinite(rr.mean)
          ? rr.mean.toFixed(0)
          : "--",
    [rr],
  );

  const detrendedSignalPoints = useMemo(() => {
    return normaliseTimeValuePoints(
      processedSignal?.detrendedSignalPoints || [],
    );
  }, [processedSignal]);

  const detrendedSignalMaxSecs = useMemo(() => {
    return getTimeSeriesMaxSecs(
      processedSignal?.timeSeconds,
      getPointSeriesMaxSecs(detrendedSignalPoints, 1),
    );
  }, [processedSignal, detrendedSignalPoints]);

  const spirometryMaxSecs = useMemo(() => {
    return getTimeSeriesMaxSecs(processedSignal?.spirometryTimeSeconds, 60);
  }, [processedSignal]);

  const normalisedSignalTracePoints = useMemo(() => {
    return normaliseTimeValuePoints(
      processedSignal?.spirometrySignalTracePoints || [],
    );
  }, [processedSignal]);

  const additionalMetricsRecord = useMemo(
    () =>
      buildSpirometryAdditionalMetricsRecord(
        additionalMetrics,
        rr,
        signalQuality,
        spirometryQuality,
      ),
    [additionalMetrics, rr, signalQuality, spirometryQuality],
  );

  const additionalMetricsFields = useMemo(
    () => buildSpirometryAdditionalMetricsFields(t),
    [t],
  );

  // const mpmDetrendedSignal = useMemo(
  //   () => pickCardiopulmonaryDetrendedSignal(data),
  //   [data],
  // );

  const details = SHOW_FULL_RESULTS ? (
    <section className="w-full p-4 pt-2 flex flex-col gap-4 text-center">
      <div className="w-full text-left">
        <h2 className="subtitle-text mb-2">
          {t("result.spiro.signalFullDetrended", "Full Detrended Signal")}
        </h2>

        <SignalChart
          smoothSignal={detrendedSignalPoints}
          rawSignal={[]}
          peaks={[]}
          troughs={[]}
          maxSecs={detrendedSignalMaxSecs}
          height={196}
        />
      </div>

      <div className="w-full text-left">
        <h2 className="subtitle-text">
          {t("result.spiro.signalSpiroSegment", "Spirometry Segment")}
        </h2>

        <SignalChart
          smoothSignal={normalisedSignalTracePoints}
          rawSignal={[]}
          peaks={[]}
          troughs={[]}
          maxSecs={spirometryMaxSecs}
          height={220}
          tickStepSize={0.5}
        />
      </div>

      <div className="w-full text-left">
        <FlowLoopChart
          title={t("result.spiro.spiroFlowLoop", "Spirometry Flow Loop")}
          points={processedSignal?.spirometryFlowLoop}
          xTitle={t("result.spiro.volumeProxy", "Volume")}
          yTitle={t("result.spiro.flowProxy", "Flow")}
          height={280}
        />
      </div>

      <div className="w-full text-left">
        <FlowVolumeLoopsSection
          recordJson={{
            flowLoopBreaths: processedSignal?.flowLoopBreaths || [],
            flow_loop: {
              summary_metrics: {
                meanExhaleInhaleAreaRatio:
                  additionalMetrics?.meanExhaleInhaleAreaRatio ?? null,
                minBreathDurationSeconds:
                  additionalMetrics?.minBreathDurationSeconds ?? null,
                maxBreathDurationSeconds:
                  additionalMetrics?.maxBreathDurationSeconds ?? null,
                meanBreathDurationSeconds:
                  additionalMetrics?.meanBreathDurationSeconds ?? null,
              },
            },
          }}
          hideSpirometryManeuverBreaths={true}
          showOverlay={true}
          showMetricBar={false}
          showMetricsTable={false}
        />
      </div>

      <div className="w-full text-left">
        <h2 className="subtitle-text mb-2">
          {t("result.spiro.additionalMetrics", "Additional metrics")}
        </h2>

        <MetricsKVTable
          record={additionalMetricsRecord}
          fields={additionalMetricsFields}
        />
      </div>
    </section>
  ) : null;

  return (
    <ResultViewScaffold
      title={t("result.spiro.completeTitle", "Spirometry scan complete")}
      recordingTime={recordingTime}
      onFinish={onFinish}
      details={details}
    >
      <Spacer />
      <ResultSummarySection
        icon={<SignalQuality />}
        title={t("result.qualityTitle", "Recording Quality")}
        disabled={resultQualityIsDisabled}
      >
        <ResultSummaryRow
          label={t("result.spiro.signalQuality", "Signal Quality")}
          value={signalQualityLabel}
          disabled={resultQualityIsDisabled}
        />
        <ResultSummaryRow
          label={t("result.spiro.spirometryQuality", "Spirometry Quality")}
          value={spirometryQualityLabel}
          disabled={resultQualityIsDisabled}
        />

        <ResultQualityNotice
          messages={resultQualityNotice ? [resultQualityNotice] : []}
          hints={resultQualityHints}
          hintsTitle={t("result.spiro.qualityHints.title", "What to improve")}
          linkTo="/guidance/spirometry"
          ctaLabel={t("result.spiro.guidanceCta", "Recording Tips")}
          className="mt-auto w-full pb-2 flex flex-col gap-4"
          onLinkClick={() =>
            trackEvent("spirometry_result_click_guidance", {
              signal_quality: signalQuality,
              spirometry_quality: spirometryQuality,
              zeroed_metrics: zeroedMetrics,
            })
          }
        />
      </ResultSummarySection>
      {SHOW_FULL_RESULTS && (
        <>
          <Spacer />
          <ResultSummarySection
            icon={<RespiratoryRate />}
            title={t("result.spiro.spirometryTitle", "Spirometry")}
            disabled={resultQualityIsDisabled}
          >
            <ResultSummaryRow
              label={t("result.spiro.fev1", "FEV1")}
              value={fev1Display}
              unit={t("result.spiro.FEV1Unit", "L")}
              disabled={resultQualityIsDisabled}
            />
            <ResultSummaryRow
              label={t("result.spiro.fvc", "FVC")}
              value={fvcDisplay}
              unit={t("result.spiro.FVCUnit", "L")}
              disabled={resultQualityIsDisabled}
            />
            <ResultSummaryRow
              label={t("result.spiro.fev1FvcRatio", "FEV1/FVC")}
              value={fev1FvcRatioDisplay}
              disabled={resultQualityIsDisabled}
            />
            <ResultSummaryRow
              label={t("result.spiro.pef", "PEF")}
              value={pefDisplay}
              unit={t("result.spiro.PEFUnit", "L/min")}
              disabled={resultQualityIsDisabled}
            />
          </ResultSummarySection>

          <Spacer />

          <ResultSummarySection
            icon={<LungIcon />}
            title={t("result.spiro.respiratory", "Respiratory")}
            disabled={resultQualityIsDisabled}
          >
            <ResultSummaryRow
              label={t("result.resp.rrLongLabel", "Respiratory Rate")}
              value={rrDisplay}
              unit={t("result.resp.rrUnit", "bpm")}
              disabled={resultQualityIsDisabled}
            />{" "}
            {/* <section className=" w-full">
              {mpmDetrendedSignal.points.length ? (
                <SignalChart
                  smoothSignal={mpmDetrendedSignal.points}
                  rawSignal={[]}
                  peaks={[]}
                  troughs={[]}
                  height={196}
                  maxSecs={mpmDetrendedSignal.maxSecs}
                />
              ) : (
                <div className="muted-text">
                  {t("result.resp.noSignal", "No signal trace available.")}
                </div>
              )}
            </section> */}
          </ResultSummarySection>
        </>
      )}
    </ResultViewScaffold>
  );
}

const SpirometryResultView = memo(SpirometryResultViewImpl);

SpirometryResultView.isReady = isSpirometryResultReady;

export default SpirometryResultView;
